import {
  jWebReady,
  CDMObject,
  DataCollectionEventNames, DataValveCDMEventData, EventInfo, FilterCDMTreesOptions,
  FilterCDMTreesResult,
  FilterLEDMTreesOptions,
  FilterLEDMTreesResult, IsDataAllowedResult, LEDMObject, Subscriber, ValveFilterErrorType, EventServicePluginError, ValveFilterError,
  IsDataAllowedOptions,
  ValveControllerMetadata
} from '@jarvis/jweb-core';
import { bindingsMetadataKey, getConfigurationError, getWindowValues } from '../client/utils/enum';
import buildEnvelopAndSendNotification from '../helpers/buildEnvelopAndSendNotification';
import { setInvalidateValveControllerMetaDataInstore } from '../helpers/checkInvalidateMetaData';
import { DataValveFilterError } from '../helpers/dataValveError';
import { oneTrustFiltering, tccsFiltering, valveControllerFiltering } from '../helpers/filteringMethods';
import { logger } from '../helpers/logger';
import isGetBindingsParamValid from '../helpers/validateData';
import { getApplicationContextFromValueStore, getValveControllerMetadataFromValueStore } from '../helpers/valueStoreHelpers';
import { sanitizeLEDM } from '../LEDMFilter/sanitize';
import { Queue } from '../Queue/Queue';
import { isDataCollectionPluginWeb } from '../web';
import { DataCollectionError } from '../helpers/dataCollectionError';
import { Accumulator, evaluateBatchLogic } from '../Queue/Accumulator';
import { QueueItemStatus } from '../Queue/queueHelpers';
import { FilterObject } from '../CDMFilter/filterTypes';
import { getCachedBindings } from '../helpers/CacheStorage';
import { PACKAGE_NAME,PACKAGE_VERSION } from '../packageInfo';
import {
  Configuration, ConfigurationProvider, QueueDefaults, validateBatchConfig, defaultIsBatchEnabled, validateQueueConfigValue,
  defaultProcessingState,
  BatchConfiguration
} from './dataCollectionServiceTypes';

class DataCollectionServiceSingleton {

  configurationProvider: ConfigurationProvider | undefined;
  setConfigurationProvider(configurationProvider: ConfigurationProvider) {
    this.configurationProvider = configurationProvider;
    this.configurationProvider.configuration.preConsentEventAccumulation =
      configurationProvider?.configuration?.preConsentEventAccumulation || false;
    this.configurationProvider.configuration.delayedDataPersistenceAllowed =
      configurationProvider?.configuration?.delayedDataPersistenceAllowed || false;
    this.configurationProvider.configuration.eventState = configurationProvider?.configuration.eventState
      ? configurationProvider.configuration.eventState
      : defaultProcessingState;
    const { queueConfiguration, isCustomBatchConfigEnabled, isBatchingEnabled, batchConfiguration } =
      configurationProvider.configuration;

    const setupQueueConfiguration = () => {
      if (queueConfiguration) {
        Queue.publishRetries = validateQueueConfigValue(queueConfiguration.publishRetries, QueueDefaults.PUBLISH_RETRIES);
        Queue.queueItemTTLInHours = validateQueueConfigValue(queueConfiguration.queueItemTTLInHours, QueueDefaults.QUEUE_ITEM_TTL_IN_HOURS);
        Queue.publishRetryDelay = validateQueueConfigValue(queueConfiguration.publishRetryDelay, QueueDefaults.PUBLISH_RETRY_DELAY);
        Queue.queueSizeLimit = validateQueueConfigValue(queueConfiguration.queueSizeLimit, QueueDefaults.QUEUE_SIZE_LIMIT);
      }
    };


    // Simplify batch configuration logic
    const setupBatchConfiguration = () => {
      if (isCustomBatchConfigEnabled) {
        this.setBatchConfiguration(isBatchingEnabled, batchConfiguration);
      } else {
        this.setBatchConfiguration();
      }
    };
    // Execute the refactored functions
    setupQueueConfiguration();
    setupBatchConfiguration();
  }

  setBatchConfiguration(isBatchingEnabled?: boolean, batchConfiguration?: BatchConfiguration) {
    if (!this.configurationProvider) {
      logger.error('DataCollectionService::setBatchConfiguration: configurationProvider is not set');
      return;
    }
    logger.log('DataCollectionService::UserSentBatchConfiguration: isBatchingEnabled:' + isBatchingEnabled + 'batchConfiguration:'+ batchConfiguration);
    const { configuration } = this.configurationProvider;
    // in Web batching is disabled
    configuration.isBatchingEnabled = defaultIsBatchEnabled;
    configuration.batchConfiguration = validateBatchConfig(logger, batchConfiguration);
    logger.warn('DataCollectionService::SetBatchConfiguration::isBatchingEnabled:'+ configuration.isBatchingEnabled + 'batchConfiguration:'+ configuration.batchConfiguration);
    // Re-evaluate the batch logic and check for flush on disabling batching
    evaluateBatchLogic();
    Accumulator.checkBatchEnabledAndFlush();
    // When the eventState is changed from Processing to Ephemeral Pause, the queue should be paused
    Queue.processPausedQueueItems();
  }

  getConfiguration(): Configuration | undefined {
    return this.configurationProvider?.configuration;
  }
  async fetchBindingsIfValid(filterMetadata: ValveControllerMetadata) {
    const bindingsParamsValidationResult = isGetBindingsParamValid(filterMetadata);
    if (bindingsParamsValidationResult === '') {
      const bindings = await getCachedBindings(filterMetadata);
      return bindings;
    } else {
      throw new DataValveFilterError(ValveFilterErrorType.valveControllerMetadataError, bindingsParamsValidationResult, 'Cannot call getBindings with invalid valveControllerMetadata');
    }
  }

  private checkBindingFallbackApproval(featureName: string, filterOptions?: { allowBindingFallback?: boolean }) {
    if (
      filterOptions &&
      Object.prototype.hasOwnProperty.call(filterOptions, 'allowBindingFallback') &&
      filterOptions.allowBindingFallback === false
    ) {
      throw new DataValveFilterError(
        ValveFilterErrorType.treeNotAllowed,
        `allowBindingFallback is not supported in the web only ${featureName}`,
        'Unsupported filterOptions configuration'
      );
    }
  }
  async invalidateCache(): Promise<void> {
    const windowValue = getWindowValues();
    const previousBindingData = JSON.parse(windowValue.localStorage.getItem(bindingsMetadataKey));
    if (previousBindingData) {
      setInvalidateValveControllerMetaDataInstore(Object.keys(previousBindingData));
      windowValue.localStorage.removeItem(bindingsMetadataKey);
    }
    logger.log('DataCollectionService::invalidateCache:invalidateCache');
  }

  private validateJsonFormat(cdmRawObjects: (ValveFilterError | CDMObject)[]): (ValveFilterError | CDMObject)[] {
    return cdmRawObjects.map(cdmObject => {
      if ((cdmObject as CDMObject).tree) {
        try {
          JSON.parse((cdmObject as CDMObject).tree);
        } catch (error: any) {
          cdmObject = new DataCollectionError({ functionName: 'filterCDMTrees', functionParameters: `CDMObject.treeGun=${(cdmObject as CDMObject).treeGun}` }, ValveFilterErrorType.treeNotAllowed, 'Json Parse error', error);
        }
      }
      return cdmObject;
    });
  }

  private async handleTCCSFiltering(
    cdmObjects: (ValveFilterError | CDMObject)[],
    bindings: any
  ): Promise<(ValveFilterError | CDMObject)[]> {
    const tccsResult = tccsFiltering(cdmObjects, bindings);
    const validObjects = tccsResult.filter((obj) => (obj as CDMObject).tree !== undefined);
    const errors = tccsResult.filter((obj) => (obj as ValveFilterError).errorType !== undefined);

    const filteredObjects = errors.length > 0 ? validObjects.concat(errors) : validObjects;
    if (filteredObjects.length === 0) {
      throw new DataValveFilterError(
        ValveFilterErrorType.treeNotAllowed,
        'Empty cdm object after TCCS filtering',
        'Empty cdm object after TCCS filtering'
      );
    }

    return filteredObjects;
  }
  async filterCDMTrees(
    options: FilterCDMTreesOptions,
    tccsFilterFlag = false
  ): Promise<FilterCDMTreesResult> {
    const configuration = this.getConfiguration();
    const cdmRawObjects: (ValveFilterError | CDMObject)[] = options.cdmObjects;
    let filteredResults: (ValveFilterError | CDMObject)[];
    try {
      this.checkBindingFallbackApproval('filterCDMTrees', options.filterOptions);
      if (!configuration) {
        throw getConfigurationError();
      };
      const filterMetadata = await getValveControllerMetadataFromValueStore(options?.filterMetadata);
      const applicationContext = await getApplicationContextFromValueStore();
      // checking for json format
      let cdmObjects = this.validateJsonFormat(cdmRawObjects);
      filteredResults = cdmRawObjects.map(cdmObject => new DataCollectionError({ functionName: 'filterCDMTrees', functionParameters: `CDMObject.treeGun=${(cdmObject as CDMObject).treeGun}` }, ValveFilterErrorType.treeNotAllowed, 'Filtering not allowed', 'Invalid filtering flags'));
      if (configuration?.useOneTrustFiltering) {
        const consent = applicationContext && applicationContext?.webAppConsent ? applicationContext?.webAppConsent : '';
        const oneTrustResult = oneTrustFiltering(cdmObjects, consent);
        const result = oneTrustResult.map((res, index) => {
          if (res instanceof DataValveFilterError) {
            return new DataCollectionError({ functionName: 'filterCDMTrees', functionParameters: `CDMObject.treeGun=${(cdmObjects[index] as CDMObject).treeGun}` }, ValveFilterErrorType.treeNotAllowed, res.reason, 'Invalid resource id or Invalid telemetry consent');
          }
          return res as CDMObject;
        });
        const error = result.find(res => res instanceof DataCollectionError);
        if (error) {
          return { results: result as [ValveFilterError] };
        }
        filteredResults = result;
        cdmObjects = filteredResults;
      }
      if (configuration?.useValveControllerFiltering) {
        const bindings = await this.fetchBindingsIfValid(filterMetadata);

        if (tccsFilterFlag) {
          cdmObjects = await this.handleTCCSFiltering(cdmObjects, bindings);
        }

        filteredResults = valveControllerFiltering(cdmObjects, bindings);

      }
    } catch (err: any) {
      let errorType = ValveFilterErrorType.treeNotAllowed;
      let reason = 'Unexpected Error';
      let exception = err;
      if (err instanceof DataValveFilterError) {
        errorType = err.errorType;
        reason = err.reason || '';
        exception = err.exception;
      }
      const cdmError = cdmRawObjects.map(cdmObject => new DataCollectionError({ functionName: 'filterCDMTrees', functionParameters: `CDMObject.treeGun=${(cdmObject as CDMObject).treeGun}` }, errorType, reason, exception));
      return { results: cdmError as [ValveFilterError] };
    }
    return {
      results: filteredResults as [ValveFilterError | CDMObject]
    };
  }

  async filterLEDMTrees(
    options: FilterLEDMTreesOptions
  ): Promise<FilterLEDMTreesResult> {
    const configuration = this.getConfiguration();
    // @ts-ignore
    try {
      this.checkBindingFallbackApproval('filterLEDMTrees', options.filterOptions);
      if (!configuration) {
        throw getConfigurationError();
      }
      options.filterMetadata = await getValveControllerMetadataFromValueStore({ ...options.filterMetadata });
      const bindingsParamsValidationResult = isGetBindingsParamValid(options.filterMetadata);
      if (bindingsParamsValidationResult === '') {
        const bindings = await getCachedBindings(options.filterMetadata);
        const result = options.ledmObjects.map(ledmObject => {
          const ledmResult = sanitizeLEDM(ledmObject, bindings);
          if (ledmResult instanceof DataValveFilterError) {
            return { invocationDetails: { functionName: 'filterLEDMTrees', functionParameters: `LEDMObject.resourceUri=${ledmObject.resourceUri}` }, errorType: ledmResult.errorType, reason: ledmResult.reason, exception: ledmResult.exception };
          }
          return ledmResult;
        });
        logger.log('DataCollectionService::filterLEDMTrees::results:', result);
        return {
          results: result as [ValveFilterError | LEDMObject]
        };
      } else {
        throw new DataValveFilterError(ValveFilterErrorType.valveControllerMetadataError, bindingsParamsValidationResult, 'Cannot call getBindings with invalid valveControllerMetadata');
      }
    } catch (error: any) {
      let errorType = ValveFilterErrorType.treeNotAllowed;
      let reason = 'Unexpected Error';
      let exception = error;
      if (error instanceof DataValveFilterError) {
        errorType = error.errorType;
        reason = error.reason || '';
        exception = error.exception;
      }
      const cdmError = options.ledmObjects.map(ledmObject => new DataCollectionError({ functionName: 'filterLEDMTrees', functionParameters: `LEDMObject.resourceUri=${ledmObject.resourceUri}` }, errorType, reason, exception));
      return { results: cdmError as [ValveFilterError] };
    }
  }

  async isDataAllowed(options: IsDataAllowedOptions): Promise<IsDataAllowedResult> {
    const configuration = this.getConfiguration();
    const invocationDetails = { functionName: 'isDataAllowed', functionParameters: `IsDataAllowedOptions.dataCategory=${options.dataCategory}` };
    if (!configuration) {
      return {
        result: new DataCollectionError(invocationDetails, ValveFilterErrorType.configurationError, 'Minimum Configuration is required', 'configuration is missing')
      };
    }
    try {
      this.checkBindingFallbackApproval('isDataAllowed', (options as { filterOptions?: { allowBindingFallback?: boolean } }).filterOptions);
      options.filterMetadata = await getValveControllerMetadataFromValueStore({ ...options.filterMetadata });
      const bindingsParamsValidationResult = isGetBindingsParamValid(options.filterMetadata);
      if (bindingsParamsValidationResult === '') {
        const results = await getCachedBindings(options.filterMetadata);
        const allowedDataCategoriesType = results.bindings?.filters?.cdm?.raw;

        if (allowedDataCategoriesType) {
          const isDataAllowed = allowedDataCategoriesType.find((category: FilterObject) => category?.resourceId === options.dataCategory);
          return {
            result: !!isDataAllowed
          };
        }
        return {
          result: new DataCollectionError(invocationDetails, ValveFilterErrorType.valveControllerAPIError, bindingsParamsValidationResult, 'ValveControllerAPIError', results)
        };
      } else {
        return {
          result: new DataCollectionError(invocationDetails, ValveFilterErrorType.valveControllerMetadataError, bindingsParamsValidationResult, 'Cannot call getBindings with invalid valveControllerMetadata')
        };
      }
    } catch (error: any) {
      logger.error('DataCollectionService::isDataAllowed::error:', error);
      return {
        result: new DataCollectionError(invocationDetails, ValveFilterErrorType.valveControllerAPIError, 'ValveControllerAPIError', error)
      };
    }
  }

  async handleCdmEvent(eventInfo: EventInfo) {
    if (eventInfo.eventName === DataCollectionEventNames.cdmEvent) {
      await buildEnvelopAndSendNotification(eventInfo.eventData as DataValveCDMEventData);
    }
  };

  async flushQueue() {
    await Accumulator.flushQueue();
  }

  async getPendingQueueItems(){
    const allItems = await Queue.getAllPendingItems(QueueItemStatus.pending);
    return allItems;
  }
}

const initializeService = async (service: DataCollectionServiceSingleton) => {
  jWebReady.then((jweb) => {
    const dataCollectionPlugin = jweb.Plugins?.DataCollection;
    const eventServicePlugin = jweb.Plugins?.EventService;
    if (dataCollectionPlugin === undefined || eventServicePlugin === undefined) {
      console.warn('DataCollectionService::initialize: Missing DataCollection and/or EventService plugins');
      return;
    }
    if (!isDataCollectionPluginWeb(dataCollectionPlugin) || dataCollectionPlugin.getDataCollectionService === undefined) {
      console.warn('DataCollectionService::initialize: Native DataCollection plugin active in the environment');
      return;
    }
    if (dataCollectionPlugin.getDataCollectionService() !== service) {
      console.warn('DataCollectionService::initialize: DataCollectionServiceSingleton instance mismatch');
      return;
    }
    console.log('Web Jarvis PackageName: ' + PACKAGE_NAME +
      ' Version: ' + PACKAGE_VERSION);
    eventServicePlugin
      .createSubscriber()
      .then((subscriberResult: Subscriber | EventServicePluginError) => {
        if ('subscribe' in subscriberResult) {
          subscriberResult
            .subscribe({ eventName: DataCollectionEventNames.cdmEvent }, service.handleCdmEvent)
            .then((_) => console.info('DataCollectionService::initialize: Subscribed to CDM events'))
            .catch((error: any) => console.error(`DataCollectionService::initialize::subscribe: ${error}`));
        } else {
          console.error(`DataCollectionService::initialize::createSubscriber: ${subscriberResult}`);
          return;
        }
      })
      .catch((error: any) => console.error(`DataCollectionService::initialize::createSubscriber: ${error}`));
  }).catch((error) => console.error(`DataCollectionService::initialize::jWebReady: ${error}`));
};

export const dataCollectionService = new DataCollectionServiceSingleton();

initializeService(dataCollectionService);
